/**
 * Paymint Recent Activities Card
 * 최근 Paymint 결제 활동 내역 카드
 */

import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface Activity {
  type: string;
  id: string;
  owner_name: string;
  plan_name: string;
  amount: number;
  status: string;
  transaction_type: string;
  created_at: string;
  error_message?: string;
}

interface PaymintActivitiesData {
  activities: Activity[];
}

export const PaymintActivitiesCard: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/v1/admin/payment/recent-activities?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('최근 활동 조회 실패');
      }

      const result = await response.json();
      if (result.success) {
        setActivities(result.data.activities);
      } else {
        throw new Error(result.message || '알 수 없는 오류');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '활동 내역 조회 중 오류 발생');
      console.error('Paymint Activities Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;

    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const getStatusBadge = (status: string, transactionType: string) => {
    if (status === 'success') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          {transactionType === 'payment' ? '✅ 결제 성공' : '💸 환불 완료'}
        </span>
      );
    } else if (status === 'failed') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          ❌ 결제 실패
        </span>
      );
    } else if (status === 'pending') {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          ⏳ 처리 중
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">📜 최근 결제 활동</h2>
          <p className="text-sm text-gray-600 mt-1">최근 20건의 Paymint 거래 내역</p>
        </div>
        <button
          onClick={fetchActivities}
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
        >
          🔄 새로고침
        </button>
      </div>

      {/* Activities List */}
      {activities.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          최근 활동 내역이 없습니다.
        </div>
      ) : (
        <div className="overflow-y-auto max-h-96">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  시간
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  오너
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  플랜
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  금액
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  비고
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.map((activity) => (
                <tr key={activity.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(activity.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {activity.owner_name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {activity.plan_name}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-semibold ${
                    activity.status === 'success' ? 'text-green-600' :
                    activity.status === 'failed' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {formatCurrency(activity.amount)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(activity.status, activity.transaction_type)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {activity.error_message ? (
                      <span className="text-red-600 text-xs" title={activity.error_message}>
                        🚨 {activity.error_message.substring(0, 30)}...
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer Stats */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-600">총 건수</p>
            <p className="text-lg font-bold text-gray-900">{activities.length}건</p>
          </div>
          <div>
            <p className="text-xs text-gray-600">성공</p>
            <p className="text-lg font-bold text-green-600">
              {activities.filter(a => a.status === 'success').length}건
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-600">실패</p>
            <p className="text-lg font-bold text-red-600">
              {activities.filter(a => a.status === 'failed').length}건
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
